import axios from 'axios';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useSWR, { BareFetcher } from 'swr';

import * as apiSdk from '@gmm/sdk';
import {
  CurrentStatusIndexResponse as Response,
  CurrentStatusIndexItem as Item,
} from '@gmm/sdk-voodoo/currentStatuses';
import { CurrentStatus } from '@gmm/sdk-voodoo/shared';
import { HandEventStatus, OverrideProblemEvent } from '@gmm/sdk/ws';
import { isTypedItem } from '@gmm/ui';
import { useCurrentTeacher } from '~/lib/hooks/useCurrentTeacher';
import { useSnackbar } from '~/lib/hooks/useSnackbar';
import { useMigratedEndpoint } from '~/lib/hooks/voodoo';
import { ReadOnlyOptions } from '~/lib/sdk';
import { sortBy } from '~/lib/sortHelper';
import { sdkFetcher, swrKeyFor, voodooSdkFetcher } from '~/lib/swr';

const isHandEventStatus = (status: unknown): status is HandEventStatus =>
  isTypedItem(status) &&
  (status.type === 'HandRaised' || status.type === 'HandLowered');

interface UseCurrentStatus {
  clearHelp: (studentInClassId: string) => Promise<void>;
  getStudentHelpOrder: (student: Item) => number;
  mutate: () => Promise<Response | undefined>;
  students: Response | undefined;
  swrKey: ReturnType<typeof swrKeyFor>;
  updateStudentProblem: (notification: OverrideProblemEvent) => Promise<void>;
  updateStudentStatus: (
    status: CurrentStatus | HandEventStatus,
  ) => Promise<void>;
}

export const useCurrentStatus = ({
  readOnly,
}: ReadOnlyOptions = {}): UseCurrentStatus => {
  const { classId } = useParams<{ classId: string }>();
  const { enqueueGenericError, enqueueSuccess } = useSnackbar();
  const [t] = useTranslation(['currentStatus']);
  const [t2] = useTranslation(['studentDetail']);
  const isVoodooEndpoint = useMigratedEndpoint('class_current_status-list');
  const fetcher = isVoodooEndpoint ? voodooSdkFetcher : sdkFetcher;
  const swrKey = useCurrentStatusSwrKey(classId);

  const {
    data: students,
    mutate,
    mutateDraft,
  } = useSWR<Response>(swrKey, {
    fetcher: fetcher as BareFetcher<Response>,
    onError: () => enqueueGenericError(),
    revalidateIfStale: !readOnly,
    revalidateOnReconnect: !readOnly,
  });

  const getStudentHelpOrder = (student: Item): number => {
    if (!students) return 0;
    const studentsWithHandsRaised = students.filter(
      student => student.help! > -1,
    );
    const sortedHelpStudents = sortBy<Item>('help')(studentsWithHandsRaised);

    return sortedHelpStudents.indexOf(student) + 1;
  };

  const updateStudentProblem = async (
    notification: OverrideProblemEvent,
  ): Promise<void> => {
    await mutateDraft(draft => {
      const student = draft.find(
        ({ studentInClassId }) =>
          studentInClassId === notification.studentInClassId,
      );

      if (!student) {
        return;
      }

      const helpRequestedProblem = student.helpRequestedProblem;
      const overriddenProblem = notification.helpRequestedProblem;

      if (
        helpRequestedProblem?.overrideId !== undefined &&
        helpRequestedProblem.overrideId === overriddenProblem.overrideId
      ) {
        student.helpRequestedProblem = overriddenProblem;
      }

      // Triggers a re-fetch of problem state without treating every override
      // as a help request.
      student.statusReceivedTimestamp = new Date().toISOString();

      if (notification.teacherTriggered)
        enqueueSuccess(t2('studentDetail:success.problemOverridden'));
    }, false);
  };

  const updateStudentStatus = async (
    status: CurrentStatus | HandEventStatus,
  ): Promise<void> => {
    if (isEmpty(status) || !status.studentInClassId) {
      return;
    }

    const wasHandRaisedOrLowered = isHandEventStatus(status);
    const wasHandLowered = wasHandRaisedOrLowered
      ? status.type === 'HandLowered'
      : false;
    const hasAssignment = 'exercise' in status;

    await mutateDraft(draft => {
      const student = draft.find(
        ({ studentInClassId }) => studentInClassId === status.studentInClassId,
      );

      if (student) {
        // Copies all properties from status onto student directly (shallow merge).
        // When updateStudentStatus is called due to a message from the server
        // that changes lastCorrect, this will trigger reactive updates based
        // on a difference in `selectedStudent`.
        Object.assign(student, status);
        // Triggers a re-fetch of problem state if teacher is viewing
        // this student's current problem.
        student.statusReceivedTimestamp = new Date().toISOString();

        // Prevents missing potential exercise from not updating
        if (!hasAssignment && !wasHandRaisedOrLowered) {
          student.exercise = undefined;
        }

        if (wasHandLowered) delete student.helpRequestedProblem;
      }
    }, false);
  };

  const clearHelp = async (studentInClassId: string): Promise<void> => {
    try {
      await mutateDraft(draft => {
        const found = draft.find(
          student => student.studentInClassId === studentInClassId,
        );

        if (found) {
          delete found.help;
          delete found.helpRequestedProblem;
        }
      }, false);

      const config = apiSdk.helpRequests.destroy(studentInClassId);

      await axios.request(config);

      enqueueSuccess(t('currentStatus:clearHelpSuccess'));
    } catch {
      await mutate();
      enqueueGenericError();
    }
  };

  return {
    clearHelp,
    getStudentHelpOrder,
    mutate,
    students,
    swrKey,
    updateStudentProblem,
    updateStudentStatus,
  };
};

export const useCurrentStatusSwrKey = (
  classId: string,
): ReturnType<typeof swrKeyFor> => {
  const isVoodooEndpoint = useMigratedEndpoint('class_current_status-list');
  const { data: teacher } = useCurrentTeacher();

  return swrKeyFor(() => {
    if (isVoodooEndpoint) {
      return teacher ? ['currentStatuses.list', teacher.id, classId] : null;
    } else {
      return ['currentStatuses.list', classId];
    }
  });
};
